import './CreateEditUserModal.scss'
import LoadingSpinner from "../../LoadingSpinner/LoadingSpinner";
import React, {useContext, useEffect, useState} from "react";
import {Modal} from "../../Smart/Modal/Modal";
import {createUsersForOrganizations, getUserAPI} from "../../../Services/api";
import {filter, find, get, map} from "lodash";
import {GridReactContext} from "../../../Utils/DataGridWrapper/GridContext";
import {FormInput} from "../../Form/Input/FormInput";
import {Button} from "../../Form/Button/Button";
import {buttonColors} from "../../Pickers/InputTheme";
import {Alert, FormControlLabel, IconButton, Switch} from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import {MessageTypeSelectionButtons} from "../../Notifications/MessageTypeSelectionButtons/MessageTypeSelectionButtons";
import {FeatureFlags, MessageTypes} from "../../../Utils/Constants";
import {isEmailValid, validateIdNumber} from "../../../Utils/functions";
import {CompanyReactContext} from "../../../contexts/CompanyContext";
import {MultiSelectAutocomplete} from "../../Form/AutoComplete/AutoComplete";
import {useUserAdminOrgs} from "../../Hooks/UseUserAdminOrgs";
import {I18nReactContext} from "../../../contexts/I18nContext";
import {InternationalPhoneInput} from "../../Form/InternationalPhoneInput/InternationalPhoneInput";
import {InternationalPhoneInputDisplayOnly} from "../../Form/InternationalPhoneInputDisplayOnly/InternationalPhoneInputDisplayOnly";
import {isValidPhoneNumber} from 'react-phone-number-input';
import {PhoneEditModal} from "../PhoneEditModal/PhoneEditModal";

export const CreateEditUserModal = ({
                                        createEditUserModalOpen,
                                        closeCreateEditUserModal,
                                        onSuccess,
                                        editMode,
                                        userId
                                    }) => {
    const {adminOrganizationOptions} = useUserAdminOrgs({})
    const creationMode = !editMode
    const [isLoading, setIsLoading] = useState(false)
    const [selectedOrganizations, setSelectedOrganizations] = useState([])
    const [selectedOrganization, setSelectedOrganization] = useState()
    const {i18n} = useContext(I18nReactContext)
    const gridContext = useContext(GridReactContext)
    const [firstName, setFirstName] = useState('')
    const [lastName, setLastName] = useState('')
    const [email, setEmail] = useState('')
    const [phone, setPhone] = useState('')
    const [phoneLocal, setPhoneLocal] = useState('') // Local format for display in edit mode
    const [phoneCountryCode, setPhoneCountryCode] = useState(null)
    const [sendWelcomeMessage, setSendWelcomeMessage] = useState(false)
    const [selectedMessagingTypes, setSelectedMessagingTypes] = useState([MessageTypes.SMS])
    const companyContext = useContext(CompanyReactContext)
    const [idNumber, setIdNumber] = useState('')
    const [employeeId, setEmployeeId] = useState('')
    const [isPhoneEditModalOpen, setIsPhoneEditModalOpen] = useState(false)
    const [saveError, setSaveError] = useState(false)

    const onSelectedMessagingTypeChange = (messagingType) => {
        //if it is the only one selected do not remove it

        if (selectedMessagingTypes.includes(messagingType) && selectedMessagingTypes.length > 1) {
            setSelectedMessagingTypes(selectedMessagingTypes.filter((type) => type !== messagingType))
        } else {
            let messagingTypes = [...selectedMessagingTypes, messagingType]
            //uniq array
            messagingTypes = messagingTypes.filter((value, index, self) => self.indexOf(value) === index)
            setSelectedMessagingTypes(messagingTypes)
        }
    }

    const editFirstName = (e) => {
        setFirstName(e.target.value)
    }
    const editLastName = (e) => {
        setLastName(e.target.value)
    }
    const editEmail = (e) => {
        setEmail(e.target.value)
    }
    /**
     * Handle phone value change from InternationalPhoneInput
     * Phone value is in E.164 format (e.g., '+972501234567')
     * @param {string} value - Phone number in E.164 format
     */
    const handlePhoneChange = (value) => {
        if (creationMode) {
            setPhone(value || '')
        }
    }
    const editIdNumber = (e) => {
        setIdNumber(validateIdNumber(e.target.value))
    }
    const editEmployeeId = (e) => {
        setEmployeeId(e.target.value)
    }


    const innerOnSelectOrgChange = async (value) => {
        setIsLoading(true)
        setSelectedOrganization(value)
        setIsLoading(false)
    }
    const onSelectOrgChange = (e) => {
        innerOnSelectOrgChange(e.target.value)
    }

    const fetchUser = async () => {
        const user = await getUserAPI({userId})
        const userCountryCode = get(user, 'phoneCountryCode')
        const userPhone = get(user, 'phone', '')
        const userPhoneFormatted = get(user, 'phoneFormatted', userPhone)

        setFirstName(user.firstName)
        setLastName(user.lastName)
        setEmail(user.email)
        setPhoneCountryCode(userCountryCode)
        setPhoneLocal(userPhoneFormatted) // Store formatted phone for display
        setPhone(userPhone) // Keep original phone value unchanged
        setIdNumber(user.idNumber)
        setEmployeeId(user.employeeId)

        user.organizations = filter(user.organizations, org => find(adminOrganizationOptions, (adminOrg) => adminOrg.value === org.OrganizationId))
        const userOrgs = map(user.organizations, org => {
            return find(adminOrganizationOptions, (adminOrg) => adminOrg.value === org.OrganizationId)
        })
        
        setSelectedOrganizations(userOrgs)
    }

    // Initialize phone country code from company context
    useEffect(() => {
        if (companyContext.countryCode && !phoneCountryCode) {
            setPhoneCountryCode(companyContext.countryCode)
        }
    }, [companyContext])

    // Fetch user data in edit mode
    useEffect(() => {
        const init = async () => {
            if (editMode && adminOrganizationOptions) {
                setIsLoading(true)
                await fetchUser()
                setIsLoading(false)
            }
        }
        init()
    }, [adminOrganizationOptions, editMode]);

    const createOrUpdateUser = async () => {
        const user = {
            firstName,
            lastName,
            email,
            phone, // Already in E.164 format from InternationalPhoneInput
            idNumber: validateIdNumber(idNumber),
            employeeId
        }
        let sendWelcomeMessageData;
        if (sendWelcomeMessage) {
            sendWelcomeMessageData = {
                messageTypes: selectedMessagingTypes
            }
        }
        await createUsersForOrganizations({
            organizationIds: map(selectedOrganizations, 'value'),
            users: [user],
            sendWelcomeMessage: sendWelcomeMessageData,
            // Reject to this component on error (instead of the global popup +
            // silent resolve) so onSave's catch can surface a real failure.
            handleErrorByComponent: true
        })
    }
    const canSave = () => {
        let errors = []
        if (selectedOrganizations.length === 0) {
            errors.push('organizationUnit')
        }
        if (!firstName) {
            errors.push('firstName')
        }
        if (!lastName) {
            errors.push('lastName')
        }
        // Only validate phone in creation mode (phone is read-only in edit mode)
        if (creationMode && !isValidPhoneNumber(phone, phoneCountryCode)) {
            errors.push('phone')
        }
        if (email) {
            if (!isEmailValid(email)) {
                errors.push('email')
            }
        }
        // Email is required when sending welcome message via EMAIL
        if (sendWelcomeMessage && selectedMessagingTypes.includes(MessageTypes.EMAIL) && !isEmailValid(email)) {
            errors.push('email')
        }
        return errors
    }
    const onSave = async () => {
        setIsLoading(true)
        setSaveError(false)
        try {
            // Only treat the save as successful — refresh + close — when the
            // request actually resolves. Previously onSuccess() ran in finally,
            // so a failed save (e.g. org assignment) still looked successful.
            await createOrUpdateUser()
            gridContext.triggerTableRefresh()
            onSuccess()
        } catch (e) {
            console.log(e)
            // Prefer the server's error message when it provides one; otherwise
            // fall back to a generic message (don't assume the cause).
            setSaveError(get(e, 'errorMessage') || true)
        } finally {
            setIsLoading(false)
        }
    }

    const onSendWelcomeMessageChange = (e) => {
        setSendWelcomeMessage(e.target.checked)
    }
    const getFilteredOrgs = ({query}) => {
        return filter(adminOrganizationOptions,org => org.text.toLowerCase().includes(query.toLowerCase()))
    }
    const onSelectOrgsChange = async (selectedOrganizations) => {
        setSelectedOrganizations(selectedOrganizations)
    }

    /**
     * Handle successful phone update from PhoneEditModal
     * Updates local state and re-fetches user to get updated formatted phone
     * @param {object} params - The updated phone data
     * @param {string} params.phone - New phone in E.164 format
     * @param {string} params.phoneCountryCode - New ISO country code
     */
    const handlePhoneUpdateSuccess = ({phone: newPhone, phoneCountryCode: newCountryCode}) => {
        // Re-fetch user to get updated phoneLocal (formatted)
        fetchUser()
        setIsPhoneEditModalOpen(false)
        // Trigger grid refresh (same as main save)
        onSuccess()
    }

    const errors = canSave()
    return <Modal
        open={createEditUserModalOpen}
        onClose={closeCreateEditUserModal}>
        <div className="CreateEditUserModal modalWrapper">
            <div className="modalTitle">
                <div>{editMode ? `${i18n.translate('createEditUserModal.titleEdit')} ${firstName} ${lastName}` : i18n.translate('createEditUserModal.titleCreate')}</div>
            </div>
            <div className="modalContent">
                {isLoading && <LoadingSpinner/>}

                <div className="selectWrapper">
                    {adminOrganizationOptions && adminOrganizationOptions.length && !isLoading ? <MultiSelectAutocomplete
                        selectAllText={i18n.translate('editAdminUserModal.selectAllOrgs')}
                        enableSelectAll
                        label={i18n.translate('editAdminUserModal.selectOrganizationalUnits')}
                        fetchOptions={getFilteredOrgs}
                        initialSelectedValues={selectedOrganizations}
                        onSelectionChange={onSelectOrgsChange}
                        error={errors.includes('organizationUnit') && !errors.includes('firstName')}
                         /> : ''}
                </div>
                <div className="inputsWrapper">
                    <div className="inputRow">
                        <FormInput
                            required
                            error={errors.includes('firstName')}
                            label={i18n.translate('createEditUserModal.firstName')}
                            className=""
                            value={firstName}
                            onChange={editFirstName}
                        />
                        <FormInput
                            required
                            error={errors.includes('lastName')}
                            label={i18n.translate('createEditUserModal.lastName')}
                            className=""
                            value={lastName}
                            onChange={editLastName}/>
                    </div>
                    <div className="inputRow">
                        <FormInput
                            required={sendWelcomeMessage && selectedMessagingTypes.includes(MessageTypes.EMAIL)}
                            error={errors.includes('email')}
                            label={i18n.translate('createEditUserModal.email')}
                            className=""
                            value={email}
                            onChange={editEmail}/>
                        {editMode ? (
                            <InternationalPhoneInputDisplayOnly
                                value={phoneLocal}
                                countryCode={phoneCountryCode}
                                label={i18n.translate('createEditUserModal.phone')}
                                dataTestId="create-edit-user-phone-display"
                                endAdornment={
                                    <IconButton
                                        onClick={() => setIsPhoneEditModalOpen(true)}
                                        size="small"
                                        data-testid="edit-user-phone-edit-button"
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                }
                            />
                        ) : (
                            <InternationalPhoneInput
                                value={phone}
                                onChange={handlePhoneChange}
                                defaultCountry={phoneCountryCode}
                                required={creationMode}
                                error={phone && errors.includes('phone')}
                                label={i18n.translate('createEditUserModal.phone')}
                                dataTestId="create-edit-user-phone-input"
                            />
                        )}
                    </div>
                    <div className="inputRow">
                        <FormInput
                            showErrorWhenEmpty={true}
                            error={errors.includes('idNumber')}
                            label={i18n.translate('createEditUserModal.idNumber')}
                            className=""
                            value={idNumber}
                            onChange={editIdNumber}/>
                        <FormInput
                            showErrorWhenEmpty={true}
                            error={errors.includes('employeeId')}
                            label={i18n.translate('createEditUserModal.employeeId')}
                            className=""
                            value={employeeId}
                            onChange={editEmployeeId}/>
                    </div>
                    {companyContext.featureFlags[FeatureFlags.SEND_WELCOME_MESSAGE] ?
                        <FormControlLabel control={<Switch
                            checked={sendWelcomeMessage}
                            onChange={onSendWelcomeMessageChange}
                        />} label={i18n.translate('createEditUserModal.sendWelcomeMessage')}/> : ''}

                    {sendWelcomeMessage ? <MessageTypeSelectionButtons
                            multiple={true}
                            onSelectedMessageTypeChange={onSelectedMessagingTypeChange}
                            selectedMessagingTypes={selectedMessagingTypes}
                            // excludeMessageTypes={[MessageTypes.WHATSAPP]}
                        />
                        : ''}
                </div>
                {!editMode ? <div className="userMessage">
                    {i18n.translate('createEditUserModal.userWillBeEditedIfPhoneExists')}
                </div> : ''}
                {saveError ? <Alert severity="error" className="CreateEditUserModal__error">
                    {typeof saveError === 'string' ? saveError : i18n.translate('createEditUserModal.saveFailed')}
                </Alert> : ''}
            </div>
            <div className="modalFooter">
                <Button
                    color={buttonColors.YELLOW}
                    onClick={closeCreateEditUserModal}
                    text={i18n.translate('general.close')}
                />
                <Button
                    disabled={errors.length > 0}
                    color={buttonColors.GREY}
                    onClick={onSave}
                    text={i18n.translate(`createEditUserModal.save`)}
                />
            </div>
        </div>
        {editMode && (
            <PhoneEditModal
                userId={userId}
                currentPhone={phone}
                currentCountryCode={phoneCountryCode}
                isOpen={isPhoneEditModalOpen}
                onClose={() => setIsPhoneEditModalOpen(false)}
                onSuccess={handlePhoneUpdateSuccess}
            />
        )}
    </Modal>
}